import { useEffect, useState } from "react"
import MapboxDraw from "@mapbox/mapbox-gl-draw"
import { useControl } from "react-map-gl"

export default function DrawControl(props) {
  const [draw, setDraw] = useState()
  const { setDraw: setDrawInParent, onUpdate, onCreate, onDelete, defaultMode } = props

  // Ensure parent gets the initialized draw instance (with store) only after the
  // control has been added to the map (onAdd has run).
  useEffect(() => {
    if (draw) {
      setDrawInParent(draw)
      // Always activate the default mode after draw is initialized
      if (typeof draw.changeMode === "function") {
        try {
          draw.changeMode(defaultMode || "draw_polygon")
        } catch (error) {
          // keep non-fatal and log for debugging
          console.error("Error changing draw mode:", error)
        }
      }
    }
  }, [draw, setDrawInParent, defaultMode])

  // Create a small wrapper control so we can hook into onAdd/onRemove and
  // attach event listeners only after mapbox-draw has been added to the map.
  useControl(({ map }) => {
    class DrawWrapper {
      constructor(options) {
        this.options = options
        this._draw = new MapboxDraw(options)
        // Bind handlers so we can add/remove them later
        this._onCreate = (e) => onCreate && onCreate(e)
        this._onUpdate = (e) => onUpdate && onUpdate(e)
        this._onDelete = (e) => onDelete && onDelete(e)
      }

      onAdd(mapInstance) {
        // Delegate to the real draw control; this will initialize internal store
        const container = this._draw.onAdd(mapInstance)

        // Attach map events after draw is initialized
        mapInstance.on("draw.create", this._onCreate)
        mapInstance.on("draw.update", this._onUpdate)
        mapInstance.on("draw.delete", this._onDelete)

        // Wait for map to be fully loaded before activating draw mode
        const activateDrawMode = () => {
          if (typeof this._draw.changeMode === "function") {
            try {
              this._draw.changeMode(props.defaultMode || "draw_polygon")
            } catch (error) {
              console.error("Error changing draw mode in onAdd:", error)
            }
          }
        }
        if (!mapInstance.loaded()) {
          mapInstance.once("load", activateDrawMode)
        } else {
          activateDrawMode()
        }

        // Expose the underlying draw instance to the component state
        setTimeout(() => {
          setDraw(this._draw)
        }, 0)

        return container
      }

      onRemove(mapInstance) {
        // Remove event listeners
        mapInstance.off("draw.create", this._onCreate)
        mapInstance.off("draw.update", this._onUpdate)
        mapInstance.off("draw.delete", this._onDelete)

        // Delegate cleanup
        if (typeof this._draw.onRemove === "function") {
          return this._draw.onRemove(mapInstance)
        }
      }
    }

    // Return the wrapper instance - react-map-gl will call addControl with it
    return new DrawWrapper(props)
  })

  return null
}
